import { ReactElement, useState } from 'react'
import { useForm } from '@tanstack/react-form'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from '@mui/material'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch } from 'src/store/hooks'

import { fetchLicenses } from 'src/v3/profile/profile.slice'
import { License } from 'src/v3/core/types'

const noLicense = 'None'

type LicenseRankFormProps = {
  primary: License
  secondary?: License
  licenses: License[]
  open: boolean
  setOpen: (status: boolean) => void
  onError?: (error: string) => void
}

export let LicenseRankForm = ({
  primary,
  secondary,
  licenses,
  open,
  setOpen,
  onError,
}: LicenseRankFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let [saving, setSaving] = useState(false)

  let form = useForm({
    defaultValues: {
      primary: primary.id,
      secondary: secondary?.id || noLicense,
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      axios
        .put('/api/profile/license/display-ranks', {
          primary: value.primary,
          secondary:
            value.secondary === noLicense ? undefined : value.secondary,
        })
        .then(() => dispatch(fetchLicenses()))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          onError?.(pluckError(err, 'Error Updating License Ranks'))
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 300, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Edit License Rank
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <form.Subscribe
              selector={(state) => state.values.secondary}
              children={(sec) => (
                <form.Field
                  name="primary"
                  validators={{
                    onChange: ({ value }) =>
                      !value
                        ? 'Primary License is a required field.'
                        : undefined,
                  }}
                  listeners={{
                    onChange: ({ value }) => {
                      if (value === sec) {
                        form.setFieldValue('secondary', noLicense)
                      }
                    },
                  }}
                  children={(field) => (
                    <FormControl fullWidth>
                      <InputLabel>Primary License</InputLabel>
                      <Select
                        value={field.state.value}
                        label="Primary License"
                        onChange={(e) => field.handleChange(e.target.value)}
                        sx={{ bgcolor: 'background.paper' }}
                      >
                        {licenses.map((license) => (
                          <MenuItem key={license.id} value={license.id}>
                            {license.number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.primary}
              children={(pri) => (
                <form.Field
                  name="secondary"
                  validators={{
                    onChange: ({ value }) =>
                      !value
                        ? 'Secondary License is a required field'
                        : undefined,
                  }}
                  children={(field) => (
                    <FormControl fullWidth>
                      <InputLabel>Secondary License</InputLabel>
                      <Select
                        value={field.state.value}
                        label="Secondary License"
                        onChange={(e) => field.handleChange(e.target.value)}
                        sx={{ bgcolor: 'background.paper' }}
                      >
                        <MenuItem value={noLicense}>None</MenuItem>
                        {licenses
                          .filter((l) => l.id !== pri)
                          .map((license) => (
                            <MenuItem key={license.id} value={license.id}>
                              {license.number}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
